import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { toast } from 'react-toastify';
import { ProjectTimeline } from '../../models/types';

const ProjectCreate = () => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [project, setProject] = useState({
    name: '',
    shortDescription: '',
    longDescription: '',
    budget: '',
    timeline: '' as ProjectTimeline,
    country: ''
  });

  // Major European countries
  const topCountries = [
    { code: 'FR', name: 'France' },
    { code: 'DE', name: 'Germany' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' },
    { code: 'SE', name: 'Sweden' },
    { code: 'PL', name: 'Poland' },
    { code: 'AT', name: 'Austria' }
  ];

  // Rest of the world countries
  const otherCountries = [
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'JP', name: 'Japan' },
    { code: 'CN', name: 'China' },
    { code: 'IN', name: 'India' },
    { code: 'AU', name: 'Australia' },
    { code: 'BR', name: 'Brazil' },
    { code: 'RU', name: 'Russia' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'SG', name: 'Singapore' },
    { code: 'KR', name: 'South Korea' },
    { code: 'IL', name: 'Israel' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'NO', name: 'Norway' },
    { code: 'DK', name: 'Denmark' },
    { code: 'FI', name: 'Finland' },
    { code: 'IE', name: 'Ireland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'GR', name: 'Greece' }
  ];
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProject(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) return;

    try {
      setLoading(true);
      console.log('Creating project...');

      // Get user profile to verify requester status
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        console.error('User document not found');
        toast.error('Error: User not found');
        return;
      }

      const userData = userDoc.data();
      if (userData.role !== 'requester') {
        console.error('User is not a requester');
        toast.error('Only requesters can create projects');
        return;
      }

      // Prepare project data
      const projectData = {
        userId: currentUser.uid,
        name: project.name.trim(),
        shortDescription: project.shortDescription.trim(),
        longDescription: project.longDescription.trim() || null,
        budget: project.budget ? parseFloat(project.budget) : null,
        timeline: project.timeline,
        country: project.country,
        status: 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        views: 0,
        proposals: 0
      };

      console.log('Project data:', projectData);
      
      // Add project to Firestore
      const docRef = await addDoc(collection(db, 'projects'), projectData);
      console.log('Project created with ID:', docRef.id);
      
      toast.success(t('success.created'));
      navigate(`/project/${docRef.id}`);
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error(t('error.saving'));
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="container mx-auto container-padding section-padding max-w-2xl">
      <div className="bg-white rounded-lg shadow-md card-padding">
        <h1 className="heading-md mb-6">{t('project.createNew')}</h1>
        
        <form onSubmit={handleSubmit} className="spacing-lg">
          <div>
            <label htmlFor="name" className="block text-label text-gray-700 mb-1">
              {t('project.projectName')} *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={project.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 text-body-sm"
              required
            />
          </div>
          
          <div>
            <label htmlFor="shortDescription" className="block text-label text-gray-700 mb-1">
              {t('project.shortDesc')} * (max 200 characters)
            </label>
            <textarea
              id="shortDescription"
              name="shortDescription"
              value={project.shortDescription}
              onChange={handleChange}
              rows={3}
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 text-body-sm"
              required
            ></textarea>
            <p className="text-caption mt-1">
              {project.shortDescription.length}/200 characters
            </p>
          </div>
          
          <div>
            <label htmlFor="longDescription" className="block text-label text-gray-700 mb-1">
              {t('project.longDesc')} ({t('common.optional')})
            </label>
            <textarea
              id="longDescription"
              name="longDescription"
              value={project.longDescription}
              onChange={handleChange}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 text-body-sm"
            ></textarea>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="budget" className="block text-label text-gray-700 mb-1">
                {t('project.budget')} ({t('common.optional')})
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">€</span>
                <input
                  type="number"
                  id="budget"
                  name="budget"
                  value={project.budget}
                  onChange={handleChange}
                  placeholder="5000"
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 text-body-sm"
                  min="0"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="timeline" className="block text-label text-gray-700 mb-1">
                {t('project.timeline')} *
              </label>
              <select
                id="timeline"
                name="timeline"
                value={project.timeline}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 text-body-sm"
                required
              >
                <option value="">{t('project.selectTimeline')}</option>
                <option value="1">{t('project.timeline1')}</option>
                <option value="2-3">{t('project.timeline2-3')}</option>
                <option value="3-6">{t('project.timeline3-6')}</option>
                <option value="6+">{t('project.timeline6+')}</option>
                <option value="unknown">{t('project.timelineUnknown')}</option>
              </select>
            </div>
          </div>
          
          <div>
            <label htmlFor="country" className="block text-label text-gray-700 mb-1">
              {t('profile.country')} *
            </label>
            <select
              id="country"
              name="country"
              value={project.country}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 text-body-sm"
              required
            >
              <option value="">{t('profile.selectCountry')}</option>
              
              <optgroup label={t('profile.majorEuropeanCountries')}>
                {topCountries.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </optgroup>
              
              <optgroup label={t('profile.otherCountries')}>
                {otherCountries.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>
          
          <div className="flex gap-4 justify-end">
            <button
              type="button"
              onClick={() => navigate('/project/list')}
              className="px-4 sm:px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors text-button"
            >
              {t('common.cancel')}
            </button>
            
            <button
              type="submit"
              className="bg-primary-600 text-white px-4 sm:px-6 py-2 rounded-md text-button hover:bg-primary-700 transition-colors"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('project.creating')}
                </span>
              ) : (
                t('project.createProject')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectCreate;